import React, { useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useBranch } from 'contexts/BranchContext';

// 아이콘 임포트 [미사용/주석 삭제 금지]
import homeIcon from 'assets/images/icon/home-icon.svg';
import boostIcon from 'assets/images/icon/fire-icon.svg';
import personIcon from 'assets/images/icon/person-icon.svg';
import barbellIcon from 'assets/images/icon/barbell-icon.svg';
import iptIcon from 'assets/images/icon/IPT-icon.svg';
import lyloIcon from 'assets/images/icon/LYLO-icon.svg';

// 색상 상수 정의
const activeColor = '#FCFCFA';
const inactiveColor = '#7F828C';

/**
 * 하단 네비게이션 바 컴포넌트
 * @param {object} props
 * @param {'HOME' | 'BOOST' | 'PT' | 'MY' | 'IPT' | 'LYLO'} props.activeView - 현재 활성화된 뷰의 ID
 * @param {function} props.onNotYetClick - 준비 중인 버튼 클릭 시 호출되는 함수 [추가]
 */
function BottomNavBar({ activeView, onNotYetClick, onBoostClick, onPTClick }) {
    const navigate = useNavigate();
    const { currentBranch } = useBranch();

    // 동적으로 navItems 생성
    const navItems = useMemo(() => {
        const kinds = currentBranch.availableStKinds || [];
        const items = [];

        // 항상 노출
        items.push({ id: 'HOME', label: 'HOME', icon: homeIcon, path: '/home', disabled: false });

        // stKind에 따른 노출
        if (kinds.includes('E')) {
            items.push({ id: 'BOOST', label: 'BOOST', icon: boostIcon, path: '/sb', disabled: false });
        }
        if (kinds.includes('G')) {
            items.push({ id: 'IPT', label: 'IPT', icon: iptIcon, path: '/ipt', disabled: false });
        }
        if (kinds.includes('F')) {
            items.push({ id: 'LYLO', label: 'LYLO', icon: lyloIcon, path: '/lylo', disabled: false });
        }

        // stKind에 'C'가 포함될 경우 노출 (PT)
        if (kinds.includes('C')) {
            items.push({ id: 'PT', label: 'PT', icon: barbellIcon, path: '/pt', disabled: false });
        }

        // 항상 노출 (MY)
        items.push({ id: 'MY', label: 'MY', icon: personIcon, path: '/my', disabled: false });

        return items;
    }, [currentBranch.availableStKinds]);

    // Home.js에서 가져온 스타일 로직 (동적 스타일)
    const styles = {
        ...staticStyles, // 하단의 정적 스타일 객체 참조
        navIcon: (iconUrl, isActive) => ({
            width: '24px',
            height: '24px',
            backgroundColor: isActive ? activeColor : inactiveColor,
            maskImage: `url(${iconUrl})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
        }),
    };

    const handleNavigation = (item) => {
        if (item.disabled) {
            if (onNotYetClick) onNotYetClick(item.label);
            return;
        }

        // BOOST 클릭 시, 부모가 전달한 핸들러가 있으면 우선 실행
        if (item.id === 'BOOST' && onBoostClick) {
            onBoostClick();
            return;
        }

        // PT 클릭 시, 부모가 전달한 핸들러가 있으면 우선 실행
        if (item.id === 'PT' && onPTClick) {
            onPTClick();
            return;
        }

        // 그 외에는 기본 이동
        if (item.path) {
            navigate(item.path);
        }
    };

    return (
        // Fragment(<>...</>)와 하단 div(navBarSafeArea) 제거
        // 하나의 nav 태그가 전체 하단 영역을 담당합니다.
        <nav style={styles.bottomNavBar}>
            {navItems.map((item) => {
                const isActive = item.id === activeView;
                return (
                    <button
                        key={item.id}
                        style={styles.navButton}
                        onClick={item.disabled
                            ? () => onNotYetClick(item.label)
                            : () => handleNavigation(item)
                        }
                    >
                        <div style={styles.navIcon(item.icon, isActive)} />
                        <span style={{
                            ...styles.navButtonText,
                            color: isActive ? activeColor : inactiveColor
                        }}>
                            {item.label}
                        </span>
                    </button>
                );
            })}
        </nav>
    );
}

/** @type {React.CSSProperties} */
const staticStyles = {
    bottomNavBar: {
        width: '100%',
        justifyContent: 'space-around',
        alignItems: 'center',
        backgroundColor: '#0D042D',

        // 높이를 고정하지 않고 auto로 변경 (패딩 포함하여 늘어나도록)
        height: 'auto',

        display: 'flex',
        flexShrink: 0,
        boxSizing: 'border-box',

        // 무조건 화면 최하단에 붙임
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 10,

        // [핵심] 하단 안전 영역만큼 패딩을 추가
        // 아이폰 X 이상에서는 홈 인디케이터 높이만큼 패딩이 생기고,
        // 일반 기기에서는 0px이 되어 디자인이 유지됩니다.
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingTop: '0px', // 상단 패딩 제거 (버튼 높이로 조절)
    },
    // [삭제] navBarSafeArea 스타일은 더 이상 필요하지 않습니다.

    navButton: {
        flex: 1,
        // 버튼의 높이를 명시적으로 지정하여 네비게이션 바의 기본 높이(50px)를 유지
        height: '50px',

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'inherit',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        textDecoration: 'none',
        '&:disabled': {
            cursor: 'not-allowed',
        }
    },
    navButtonText: {
        height: '13px',
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: '11px',
        lineHeight: '100%',
        letterSpacing: '0%',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
};

export default React.memo(BottomNavBar);